import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';

/* ─── Animated counter hook ─── */
function useCountUp(target, duration = 1800, start = false) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (ts) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setVal(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return val;
}

/* ─── Intersection observer hook ─── */
function useInView(threshold = 0.25) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

/* ─── Animated wave background (Subtle light-mode blend: 30% dynamic DNA) ─── */
const WaveCanvas = () => {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let t = 0;

    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };
    resize();
    window.addEventListener('resize', resize);

    const waves = [
      { yBase: 0.62, amp: 38, freq: 1.1, phase: 0.0,  speed: 0.38, alpha: 0.015 },
      { yBase: 0.68, amp: 32, freq: 1.4, phase: 0.45, speed: 0.31, alpha: 0.025 },
      { yBase: 0.74, amp: 26, freq: 1.7, phase: 0.95, speed: 0.25, alpha: 0.04 },
      { yBase: 0.81, amp: 20, freq: 2.0, phase: 1.45, speed: 0.19, alpha: 0.06 },
      { yBase: 0.88, amp: 14, freq: 2.3, phase: 2.00, speed: 0.14, alpha: 0.09 },
    ];

    const draw = () => {
      const W = canvas.offsetWidth;
      const H = canvas.offsetHeight;
      ctx.clearRect(0, 0, W, H);

      waves.forEach((wave) => {
        ctx.beginPath();
        for (let i = 0; i <= 120; i++) {
          const nx = i / 120;
          const x = nx * W;
          const y =
            wave.yBase * H +
            wave.amp * Math.sin(Math.PI * 2 * (nx * wave.freq + t * wave.speed + wave.phase)) +
            wave.amp * 0.3 * Math.sin(Math.PI * 2 * (nx * wave.freq * 2.1 + t * wave.speed * 1.3 + wave.phase * 0.7));
          i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.lineTo(W, H);
        ctx.lineTo(0, H);
        ctx.closePath();
        ctx.fillStyle = `rgba(37,99,235,${wave.alpha})`;
        ctx.fill();
      });

      t += 0.016;
      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="lp-wave-canvas" aria-hidden="true" />;
};

/* ─── Score donut ─── */
const ScoreDonut = ({ score, label, color, inView }) => {
  const animScore = useCountUp(score, 1600, inView);
  const r = 36;
  const circ = 2 * Math.PI * r;
  const dash = (animScore / 100) * circ;

  return (
    <div className="lp-donut-wrap">
      <svg width="90" height="90" viewBox="0 0 90 90" aria-hidden="true">
        <circle cx="45" cy="45" r={r} fill="none" stroke="rgba(15,23,42,0.06)" strokeWidth="7" />
        <circle
          cx="45" cy="45" r={r} fill="none"
          stroke={color} strokeWidth="7"
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
          transform="rotate(-90 45 45)"
          style={{ transition: 'stroke-dasharray 0.05s linear' }}
        />
        <text x="45" y="49" textAnchor="middle" fontSize="14" fontWeight="700" fill="var(--color-text-primary)" fontFamily="var(--font-heading)">
          {animScore}
        </text>
      </svg>
      <span className="lp-donut-label">{label}</span>
    </div>
  );
};

/* ─── Feature card ─── */
const FeatureCard = ({ icon, title, body, delay }) => (
  <div className="lp-feature-card lp-glass-card" style={{ animationDelay: delay }}>
    <div className="lp-feature-icon" aria-hidden="true">{icon}</div>
    <h3 className="lp-feature-title">{title}</h3>
    <p className="lp-feature-body">{body}</p>
  </div>
);

/* ─── Stat pill ─── */
const StatPill = ({ value, label, inView }) => {
  const numeric = parseInt(value, 10);
  const suffix = value.replace(/[0-9]/g, '');
  const animated = useCountUp(numeric, 1400, inView);
  return (
    <div className="lp-stat-pill">
      <span className="lp-stat-value">{animated}{suffix}</span>
      <span className="lp-stat-label">{label}</span>
    </div>
  );
};

/* ─── Main Landing Page ─── */
const LandingPage = () => {
  const [heroVisible, setHeroVisible] = useState(false);
  const [statsRef, statsInView] = useInView(0.3);
  const [demoRef, demoInView] = useInView(0.2);
  const [showcaseRef] = useInView(0.2);
  const [navScrolled, setNavScrolled] = useState(false);
  const [activeShowcaseTab, setActiveShowcaseTab] = useState('skills');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="lp-root">
      {/* ── Animated subtle wave (30% existing dynamic DNA) ── */}
      <WaveCanvas />

      {/* ── Subtle architectural grid overlay ── */}
      <div className="lp-grid-overlay" aria-hidden="true" />

      {/* ── Soft ambient light blobs ── */}
      <div className="lp-blob lp-blob-1" aria-hidden="true" />
      <div className="lp-blob lp-blob-2" aria-hidden="true" />

      {/* ══════════════ NAV (Modern Floating Glass Blended with Dashboard/Valley of Peace) ══════════════ */}
      <nav className={`lp-nav ${navScrolled ? 'lp-nav--scrolled' : ''}`} aria-label="Main navigation">
        <div className="lp-nav-inner">
          <Link to="/" className="lp-nav-brand" aria-label="RezFlow Home">
            <span className="brand-header-logo" aria-hidden="true" style={{ fontSize: '26px', color: '#4F46E5', lineHeight: 1 }}>⬡</span>
            <span className="brand-header-name" style={{ fontFamily: 'var(--font-heading)', fontWeight: 800, fontSize: '22px', color: '#0F172A', letterSpacing: '-0.5px', margin: 0, lineHeight: 1 }}>RezFlow</span>
          </Link>

          {/* Clean Modern Navigation Links */}
          <div className="lp-nav-center-wrap" role="list">
            <a href="#how-it-works" className="lp-nav-link" role="listitem">How It Works</a>
            <a href="#showcase" className="lp-nav-link" role="listitem">Interactive Demo</a>
            <Link to="/pricing" className="lp-nav-link" role="listitem">Pricing</Link>
          </div>

          <div className="lp-nav-ctas">
            <Link to="/login" className="lp-nav-btn-ghost">Sign in</Link>
            <Link to="/signup" className="lp-nav-btn-solid" id="nav-signup-btn">Get started free →</Link>
          </div>

          <button
            type="button"
            className="lp-mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle navigation menu"
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? '✕' : '☰'}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="lp-mobile-menu">
            <Link to="/" className="lp-nav-brand" style={{ justifyContent: 'center', marginBottom: '8px' }}>
              <span className="brand-header-logo" style={{ fontSize: '22px', color: '#4F46E5' }}>⬡</span>
              <span className="brand-header-name" style={{ fontSize: '20px', color: '#0F172A' }}>RezFlow</span>
            </Link>
            <a href="#how-it-works" className="lp-mobile-link" onClick={() => setMobileMenuOpen(false)}>How It Works</a>
            <a href="#showcase" className="lp-mobile-link" onClick={() => setMobileMenuOpen(false)}>Interactive Demo</a>
            <Link to="/pricing" className="lp-mobile-link" onClick={() => setMobileMenuOpen(false)}>Pricing</Link>
            <div className="lp-mobile-ctas">
              <Link to="/login" className="lp-mobile-signin" onClick={() => setMobileMenuOpen(false)}>Sign in</Link>
              <Link to="/signup" className="lp-mobile-signup" onClick={() => setMobileMenuOpen(false)}>Get started free →</Link>
            </div>
          </div>
        )}
      </nav>

      {/* ══════════════ HERO (70% Mockup / 30% Existing Design) ══════════════ */}
      <section className={`lp-hero ${heroVisible ? 'lp-hero--visible' : ''}`} aria-labelledby="hero-headline">
        <div className="lp-hero-content-centered">
          {/* Zero-Risk Pill Badge */}
          <div className="lp-hero-badge-pill" aria-label="Free to start and zero risk entry">
            <span className="lp-hero-badge-dot" aria-hidden="true" />
            Free to start · No credit card required
          </div>

          {/* Main Headline (90 seconds per comment) */}
          <h1 id="hero-headline" className="lp-hero-headline-centered">
            A tailored resume for every job.<br />
            <span className="lp-hero-highlight-blue">In 90 seconds.</span>
          </h1>

          {/* Descriptive Subheadline */}
          <p className="lp-hero-sub-centered">
            RezFlow reads any job description, extracts the exact keywords the ATS scanner scores on, and rewrites your resume around what the role actually requires.
          </p>

          {/* Action Button Group */}
          <div className="lp-hero-ctas-centered">
            <Link to="/signup" className="lp-hero-btn-primary" id="hero-signup-btn">
              Get my tailored resume →
            </Link>
            <Link to="/login" className="lp-hero-btn-secondary">
              Sign in
            </Link>
          </div>
        </div>

        {/* ── Horizontal Stats Strip right across the fold (No Cover Letter!) ── */}
        <div className="lp-hero-bottom-strip">
          <div className="lp-stats-strip-grid">
            <div className="lp-strip-stat-item">
              <span className="lp-strip-stat-val">90 sec</span>
              <span className="lp-strip-stat-label">average tailoring time</span>
            </div>
            <div className="lp-strip-divider" aria-hidden="true" />
            <div className="lp-strip-stat-item">
              <span className="lp-strip-stat-val">9-step</span>
              <span className="lp-strip-stat-label">AI workflow</span>
            </div>
            <div className="lp-strip-divider" aria-hidden="true" />
            <div className="lp-strip-stat-item">
              <span className="lp-strip-stat-val">ATS-first</span>
              <span className="lp-strip-stat-label">keyword matching</span>
            </div>
            <div className="lp-strip-divider" aria-hidden="true" />
            <div className="lp-strip-stat-item">
              <span className="lp-strip-stat-val">94%</span>
              <span className="lp-strip-stat-label">avg. score improvement</span>
            </div>
          </div>

          {/* Workflow Ticker Bar right above fold transition (No Cover Letter!) */}
          <div className="lp-workflow-bar">
            <span className="lp-workflow-step">Upload your master resume once</span>
            <span className="lp-workflow-arrow">→</span>
            <span className="lp-workflow-step">Paste any job description</span>
            <span className="lp-workflow-arrow">→</span>
            <span className="lp-workflow-step">Get a tailored resume and instant gap analysis</span>
          </div>
        </div>
      </section>

      {/* ── 30% Dynamic DNA: Compact Live AI Evaluation Card (Dedicated Section right below Hero fold) ── */}
      <section className="lp-live-badge-section">
        <div className="lp-hero-live-badge-wrap">
          <div className="lp-hero-live-badge lp-glass-card">
            <div className="lp-live-badge-left">
              <ScoreDonut score={94} label="Match Score" color="#524BE7" inView={heroVisible} />
            </div>
            <div className="lp-live-badge-right">
              <div className="lp-live-badge-header">
                <span className="lp-score-pulse" aria-hidden="true">
                  <span className="lp-score-pulse-dot" />
                  Live AI Calibration
                </span>
                <span className="lp-live-tag">PDF Ready</span>
              </div>
              <p className="lp-live-badge-desc">
                Keyword alignment verified across 12+ industry ATS filters.
              </p>
              <div className="lp-live-chips">
                <span className="lp-chip-micro">✓ Skills Mapped</span>
                <span className="lp-chip-micro">✓ Impact Maximized</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════ THE PROBLEM (Reframed with cohesive glass cards) ══════════════ */}
      <section id="problem" className="lp-section lp-section--problem" aria-labelledby="problem-heading">
        <div className="lp-section-inner">
          <div className="lp-section-header">
            <span className="lp-section-eyebrow">The Friction</span>
            <h2 id="problem-heading" className="lp-section-title">The ATS black box stands in your way.</h2>
            <p className="lp-section-sub">
              Modern hiring relies on automatic filtering. If your resume lacks the exact semantic structure, metrics, or keywords, it's discarded immediately.
            </p>
          </div>

          <div className="lp-problem-grid">
            <div className="lp-problem-card lp-glass-card">
              <div className="lp-problem-num">01</div>
              <h3 className="lp-problem-title">Invisible Gaps</h3>
              <p className="lp-problem-body">
                Job descriptions hide critical requirements. Missing just one key competency tells the algorithm you aren't qualified, even if you are.
              </p>
            </div>
            <div className="lp-problem-card lp-glass-card">
              <div className="lp-problem-num">02</div>
              <h3 className="lp-problem-title">Blanket Rewriting Failures</h3>
              <p className="lp-problem-body">
                Standard builders force generic templates or override your entire work history, diluting your actual personality and accomplishments.
              </p>
            </div>
            <div className="lp-problem-card lp-glass-card">
              <div className="lp-problem-num">03</div>
              <h3 className="lp-problem-title">Blind Optimization</h3>
              <p className="lp-problem-body">
                Most tools make edits and send you off without proving the results. You apply blind, hoping the score actually improved.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════ HOW IT WORKS (Reframed flow) ══════════════ */}
      <section id="how-it-works" className="lp-section lp-section--alt" aria-labelledby="how-heading">
        <div className="lp-section-inner">
          <div className="lp-section-header">
            <span className="lp-section-eyebrow">The Method</span>
            <h2 id="how-heading" className="lp-section-title">Simplicity. Powered by AI.</h2>
            <p className="lp-section-sub">
              RezFlow eliminates the manual guesswork, aligning your resume to target roles in three clean steps.
            </p>
          </div>

          <div className="lp-steps-grid">
            <FeatureCard
              delay="0ms"
              icon={
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
                  <rect x="3" y="2" width="16" height="18" rx="3" stroke="currentColor" strokeWidth="1.6"/>
                  <path d="M7 7h8M7 11h8M7 15h5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                </svg>
              }
              title="1. Locate the Gaps"
              body="Drop in your resume and target job description. Our AI compares them semantically to map matches, partial alignments, and missing credentials."
            />
            <FeatureCard
              delay="80ms"
              icon={
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
                  <path d="M11 3v2M11 17v2M3 11H1M21 11h-2M5.22 5.22l1.42 1.42M15.36 15.36l1.42 1.42M5.22 16.78l1.42-1.42M15.36 6.64l1.42-1.42" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                  <circle cx="11" cy="11" r="4" stroke="currentColor" strokeWidth="1.6"/>
                </svg>
              }
              title="2. Calibrate by Section"
              body="Choose tailored strategies (e.g. inject keywords, maximize metrics) for specific sections rather than a full overwrite. Maintain your authentic voice."
            />
            <FeatureCard
              delay="160ms"
              icon={
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
                  <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6z" stroke="currentColor" strokeWidth="1.6"/>
                </svg>
              }
              title="3. Track & Export"
              body="Watch your overall score increase through real-time version history comparison. When satisfied, export a polished, recruiter-ready PDF."
            />
          </div>
        </div>
      </section>

      {/* ══════════════ INTERACTIVE SHOWCASE (Sleek macOS window frame) ══════════════ */}
      <section id="showcase" className="lp-section lp-showcase-section" ref={showcaseRef} aria-labelledby="showcase-heading">
        <div className="lp-section-inner">
          <div className="lp-section-header">
            <span className="lp-section-eyebrow">Interactive Showcase</span>
            <h2 id="showcase-heading" className="lp-section-title">See the alignment process in action.</h2>
            <p className="lp-section-sub">
              Explore how RezFlow isolates gaps, applies custom section adjustments, and measures progress.
            </p>
          </div>

          {/* Tab Switcher */}
          <div className="lp-showcase-tabs-container">
            <div className="lp-showcase-tabs" role="tablist">
              {[
                { id: 'skills', label: '1. Skills Gap Tracker' },
                { id: 'tuning', label: '2. Section Calibration' },
                { id: 'trajectory', label: '3. Version Trajectory' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  role="tab"
                  aria-selected={activeShowcaseTab === tab.id}
                  className={`lp-showcase-tab ${activeShowcaseTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveShowcaseTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Mockup Display Box */}
          <div className="lp-showcase-mockup-wrapper lp-glass-card">
            {activeShowcaseTab === 'skills' && (
              <div className="lp-mockup-tab-content lp-fade-in" aria-label="Skills Gap Tracker Mockup">
                <div className="lp-mockup-header">
                  <div className="lp-mockup-dot-group">
                    <span className="lp-mockup-dot red"></span>
                    <span className="lp-mockup-dot yellow"></span>
                    <span className="lp-mockup-dot green"></span>
                  </div>
                  <span className="lp-mockup-title">Skills Gap Map - Target Role</span>
                </div>
                <div className="lp-mockup-body">
                  <div className="lp-skills-showcase-list">
                    {[
                      { type: 'tech', name: 'Python / FastAPI', status: 'Aligned', color: 'success', desc: 'Direct match found in experience bullet points.' },
                      { type: 'non-tech', name: 'Cross-functional Communication', status: 'Aligned', color: 'success', desc: 'Mapped to stakeholder collaborative items.' },
                      { type: 'tech', name: 'Kubernetes Deployments', status: 'Gap Injected', color: 'accent', desc: 'Auto-aligned through optimization strategy.' },
                      { type: 'non-tech', name: 'Budget & Resource Management', status: 'Missing', color: 'warning', desc: 'Identified as gap: 4 occurrences in job description.' },
                      { type: 'non-tech', name: 'Visual Design & Layout', status: 'Missing', color: 'warning', desc: 'Identified as gap: Required for design consistency sections.' }
                    ].map((item, idx) => (
                      <div className="lp-skills-row" key={idx}>
                        <div className="lp-skills-row-left">
                          <span className={`lp-skills-type-tag ${item.type}`}>{item.type}</span>
                          <span className="lp-skills-name">{item.name}</span>
                        </div>
                        <div className="lp-skills-row-right">
                          <span className="lp-skills-desc">{item.desc}</span>
                          <span className={`lp-skills-status-badge ${item.color}`}>{item.status}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeShowcaseTab === 'tuning' && (
              <div className="lp-mockup-tab-content lp-fade-in" aria-label="Section Calibration Mockup">
                <div className="lp-mockup-header">
                  <div className="lp-mockup-dot-group">
                    <span className="lp-mockup-dot red"></span>
                    <span className="lp-mockup-dot yellow"></span>
                    <span className="lp-mockup-dot green"></span>
                  </div>
                  <span className="lp-mockup-title">Section Editor - Work Experience</span>
                </div>
                <div className="lp-mockup-body">
                  <div className="lp-tuning-controls">
                    <div className="lp-tuning-select-wrap">
                      <span className="lp-tuning-label">Target Section:</span>
                      <div className="lp-tuning-select">Professional Summary & Experience</div>
                    </div>
                    <div className="lp-tuning-select-wrap">
                      <span className="lp-tuning-label">AI Strategy:</span>
                      <div className="lp-tuning-select highlight">Maximize Impact & Inject Keywords</div>
                    </div>
                  </div>
                  <div className="lp-tuning-comparison">
                    <div className="lp-tuning-pane original">
                      <div className="lp-pane-header">Original bullet point</div>
                      <div className="lp-pane-content">
                        "Responsible for managing the design team, tracking project budgets, and shipping interfaces."
                      </div>
                    </div>
                    <div className="lp-tuning-pane optimized">
                      <div className="lp-pane-header">AI Calibrated bullet point</div>
                      <div className="lp-pane-content">
                        "Directly managed a cross-functional design team to ship 12+ interfaces, while overseeing a <span className="highlight-text">Budget of $200k</span>. Optimized <span className="highlight-text">Visual Design & Layout</span> to boost user retention by 22%."
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeShowcaseTab === 'trajectory' && (
              <div className="lp-mockup-tab-content lp-fade-in" aria-label="Version Trajectory Mockup">
                <div className="lp-mockup-header">
                  <div className="lp-mockup-dot-group">
                    <span className="lp-mockup-dot red"></span>
                    <span className="lp-mockup-dot yellow"></span>
                    <span className="lp-mockup-dot green"></span>
                  </div>
                  <span className="lp-mockup-title">Version Trajectory Comparison</span>
                </div>
                <div className="lp-mockup-body">
                  <div className="lp-trajectory-grid">
                    {[
                      { version: 'V1: Initial Upload', score: 68, state: 'Risk', color: '#dc2626', kw: 54, cl: 72, imp: 66 },
                      { version: 'V2: Keyword Sync', score: 84, state: 'Strong', color: '#818cf8', kw: 92, cl: 80, imp: 78 },
                      { version: 'V3: Impact Optimization', score: 96, state: 'Ready', color: '#16a34a', kw: 98, cl: 94, imp: 95 }
                    ].map((item, idx) => (
                      <div className="lp-trajectory-card" key={idx}>
                        <div className="lp-trajectory-card-header">
                          <span className="lp-traj-ver">{item.version}</span>
                          <span className="lp-traj-state" style={{ color: item.color }}>{item.state}</span>
                        </div>
                        <div className="lp-traj-score-row">
                          <span className="lp-traj-score-label">Overall Match</span>
                          <span className="lp-traj-score-value" style={{ color: item.color }}>{item.score}%</span>
                        </div>
                        <div className="lp-traj-bar-track">
                          <div className="lp-traj-bar-fill" style={{ width: `${item.score}%`, background: item.color }}></div>
                        </div>
                        <div className="lp-traj-sub-metrics">
                          <div>Keywords: {item.kw}%</div>
                          <div>Clarity: {item.cl}%</div>
                          <div>Impact: {item.imp}%</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ══════════════ STATS (Harmonized with 90 seconds per comment) ══════════════ */}
      <section className="lp-stats-section" ref={statsRef} aria-labelledby="stats-heading">
        <h2 id="stats-heading" className="lp-sr-only">Platform statistics</h2>
        <div className="lp-stats-row">
          <StatPill value="94%" label="avg. score improvement" inView={statsInView} />
          <div className="lp-stats-divider" aria-hidden="true" />
          <StatPill value="3x" label="more interviews reported" inView={statsInView} />
          <div className="lp-stats-divider" aria-hidden="true" />
          <StatPill value="90" label="seconds to first result" inView={statsInView} />
        </div>
      </section>

      {/* ══════════════ HISTORY / TRACK RECORD (No Cover Letter) ══════════════ */}
      <section id="history" className="lp-section lp-section--alt" ref={demoRef} aria-labelledby="history-heading">
        <div className="lp-section-inner">
          <div className="lp-history-split">
            <div className="lp-history-copy">
              <span className="lp-section-eyebrow">Track Record</span>
              <h2 id="history-heading" className="lp-section-title">Every version, every attempt — all yours.</h2>
              <p className="lp-section-sub" style={{ textAlign: 'left' }}>
                RezFlow keeps a full history of every resume you've optimized,
                every score you've achieved, and every job you've targeted.
                Come back anytime, compare versions, and pick up exactly where you left off.
              </p>
              <ul className="lp-history-list" aria-label="History feature highlights">
                {[
                  'Full revision history across all sessions',
                  'Side-by-side score comparisons',
                  'One-click PDF downloads of any past version',
                  'Organised by role, company, and application date',
                ].map((item) => (
                  <li key={item} className="lp-history-list-item">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <circle cx="8" cy="8" r="7" stroke="#524BE7" strokeWidth="1.4"/>
                      <path d="M5 8l2 2 4-4" stroke="#524BE7" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/signup" className="lp-hero-btn-primary" id="history-cta-btn" style={{ marginTop: '32px', display: 'inline-flex' }}>
                Start tracking your journey →
              </Link>
            </div>

            {/* History preview card */}
            <div className={`lp-history-preview lp-glass-card ${demoInView ? 'lp-history-preview--visible' : ''}`} aria-label="History panel preview">
              <div className="lp-history-preview-header">
                <span className="lp-history-preview-title">My Optimizations</span>
                <span className="lp-history-preview-count">7 sessions</span>
              </div>
              {[
                { role: 'Senior Product Designer', company: 'Figma', score: 96, date: 'Jun 3', delta: '+12' },
                { role: 'UX Lead',                  company: 'Linear',  score: 91, date: 'May 28', delta: '+8' },
                { role: 'Product Designer',          company: 'Vercel',  score: 88, date: 'May 20', delta: '+11' },
              ].map((item, i) => (
                <div key={i} className="lp-history-row" style={{ animationDelay: `${i * 100}ms` }}>
                  <div className="lp-history-row-info">
                    <span className="lp-history-row-role">{item.role}</span>
                    <span className="lp-history-row-meta">{item.company} · {item.date}</span>
                  </div>
                  <div className="lp-history-row-right">
                    <span className="lp-history-row-score">{item.score}</span>
                    <span className="lp-history-row-delta">{item.delta}</span>
                  </div>
                </div>
              ))}
              <div className="lp-history-preview-footer">
                <ScoreDonut score={96} label="Top score" color="#524BE7" inView={demoInView} />
                <ScoreDonut score={91} label="Avg score" color="#818cf8" inView={demoInView} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════ GATEWAY CTA (Sleek cohesive card) ══════════════ */}
      <section className="lp-gateway" aria-labelledby="gateway-heading">
        <div className="lp-gateway-inner lp-glass-card">
          <div className="lp-gateway-badge" aria-hidden="true">
            <span className="lp-score-pulse-dot" style={{ background: '#524BE7' }} />
            Free to start
          </div>
          <h2 id="gateway-heading" className="lp-gateway-title">
            The role you want is<br />one optimized resume away.
          </h2>
          <p className="lp-gateway-sub">
            Join thousands of professionals who stopped sending generic resumes and started getting replies.
          </p>
          <div className="lp-gateway-ctas">
            <Link to="/signup" className="lp-hero-btn-primary" id="gateway-signup-btn">
              Create Your Free Account →
            </Link>
            <Link to="/login" className="lp-gateway-signin-link">Already have an account? Sign in →</Link>
          </div>
        </div>
      </section>

      {/* ══════════════ FOOTER (Modern SaaS Blended Design with Dashboard/Valley of Peace DNA) ══════════════ */}
      <footer className="lp-footer-modern" role="contentinfo">
        <div className="lp-footer-grid">
          {/* Column 1: Brand & Mission */}
          <div className="lp-footer-col lp-footer-brand-col">
            <Link to="/" className="lp-nav-brand" aria-label="RezFlow Home">
              <span className="brand-header-logo" aria-hidden="true" style={{ fontSize: '26px', color: '#4F46E5', lineHeight: 1 }}>⬡</span>
              <span className="brand-header-name" style={{ fontFamily: 'var(--font-heading)', fontWeight: 800, fontSize: '22px', color: '#0F172A', letterSpacing: '-0.5px', margin: 0, lineHeight: 1 }}>RezFlow</span>
            </Link>
            <p className="lp-footer-desc">
              AI-powered resume tailoring built to crack the ATS black box, optimize keyword density, and land more interviews across top industry pipelines in 90 seconds.
            </p>
            <div className="lp-footer-badges">
              <span className="lp-footer-status-badge">
                <span className="lp-status-dot" aria-hidden="true" />
                All Systems Operational
              </span>
              <span className="lp-footer-security-badge">🔒 256-bit ATS Encryption</span>
            </div>
          </div>

        </div>

        {/* Bottom copyright and Valley of Peace tag */}
        <div className="lp-footer-bottom">
          <p className="lp-footer-copyright">
            © {new Date().getFullYear()} RezFlow Innovation. All rights reserved.
          </p>
          <div className="lp-footer-bottom-right">
            <span className="lp-footer-tagline">Built for modern careers · Valley of Peace DNA</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
